import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker'
import { useAppDispatch, RootState } from '@/store';
import { getHolidayList, createHoliday, deleteHoliday } from '@/store/holiday/action';
import { getUserVenues } from '@/store/user-info/action';
import BreadcrumbLayout from '@/components/common/CustomLayout/BreadcrumbLayout';
import { ReactComponent as RiCalendarLine } from '@/assets/icons/ri-calendar-2-line.svg'
import { ReactComponent as RiAddLine } from '@/assets/icons/ri-add-line.svg'
import { ReactComponent as RiDeleteBin } from '@/assets/icons/ri-delete-bin-6-line.svg'
import { ReactComponent as RiCheckmarkLine } from '@/assets/icons/ri-checkmark-line.svg'
import Input from '@/components/common/Input';
import Button from '@/components/common/Button';
import { convertLocalToWita, convertToLocalTime } from '@/utils';
import 'react-datepicker/dist/react-datepicker.css'
import UserVenueSelect from "@/components/common/UserVenueSelect";

const venueOnHoliday = (venues: any[]) => venues.map(venue => venue.venue?.name).join(', ');

const renderExistingHolidays = (
  { holidayMethods }: any,
  existingHolidays: Holiday[],
  deleteExistingHoliday: Function
) => (
  existingHolidays.map(holiday => (
    <div className='flex items-center py-2 gap-7 w-80%'>
      <div className="h-full">
        <RiCalendarLine />
      </div>
      <div className="flex gap-7 items-center">
        <FormProvider {...holidayMethods}>
          <Input name='holidayName'
                 type='text'
                 placeholder={holiday.name}
                 disabled
          />
          <DatePicker selected={convertToLocalTime(holiday.start_date)}
                      className="h-full border border-grey-line-subtle flex flex-row justify-center items-center px-6 py-2 rounded-[3px] font-medium text-sm text-grey-dark"
                      disabled/>
          <div className="bg-grey-line-subtle h-6 w-1"/>
          <Input name="venue" type="text" placeholder="Select venue" value={venueOnHoliday(holiday.venue_holidays)} disabled/>
        </FormProvider>
      </div>
      <Button className="text-red-primary flex items-center p-0 hover:cursor-pointer"
              variant='tertierry'
              onClick={() => deleteExistingHoliday(holiday)}>
        <RiDeleteBin/>
      </Button>
    </div>
  ))
)

const renderNewHolidays = (
  { holidayMethods }: any,
  newHolidays: Holiday[],
  venues: Venue[],
  calendar: { [key: string]: number },
  setCalendar: Function,
  createNewHoliday: Function,
  removeNewHoliday: Function
) => (
  <FormProvider {...holidayMethods}>
    {newHolidays.map((holiday) => (
      <div className='flex items-center py-2 gap-7 w-80%'
           key={holiday.id}>
        <div className="h-full">
          <RiCalendarLine />
        </div>
        <div className="flex flex-grow-0 gap-7 items-center">
          <Input name='holidayName'
                 type='text'
                 placeholder='Holiday Name'
                 {...holidayMethods.register(`holiday.${holiday.id}.name`)}
          />
          <div className="w-full flex items-center">
            <Controller rules={{ required: 'Please select date' }}
                        name={`holiday.${holiday.id}.date`}
                        render={({ field }) => (
                          <DatePicker className="h-full border border-grey-line-subtle flex flex-row justify-center items-center px-6 py-2 rounded-[3px] font-medium text-sm text-grey-dark flex-grow-0"
                                      placeholderText="Select a date"
                                      selected={field.value || new Date().getTime()}
                                      onSelect={date => {
                                        setCalendar({ ...calendar, [holiday.id]: date.getTime()})
                                        holidayMethods.setValue(`holiday.${holiday.id}.date`, date.getTime())
                                      }} />
                        )}
                        {...holidayMethods.register(`holiday.${holiday.id}.date`)}
            />
            <div className="bg-grey-line-subtle h-6 w-[1px] ml-7"/>
          </div>
          <Controller
            render={({ field }) =>
              <UserVenueSelect
                useIcon={false}
                options={
                  venues.map(venue => ({
                    label: venue.name,
                    value: venue.id
                  }))
                }
                placeholder='Select venue'
                isClearable
                {...holidayMethods.register(`holiday.${holiday.id}.venue`)}
                { ...field }
              />}
            name={`holiday.${holiday.id}.venue`}/>
        </div>
        <Button className="text-red-primary flex items-center p-0 hover:cursor-pointer"
                variant='tertierry'
                onClick={() => {
                  removeNewHoliday(holiday)
                }}>
          <RiDeleteBin/>
        </Button>
        <Button className="text-green-success flex items-center p-0 hover:cursor-pointer"
                variant='tertierry'
                onClick={() => {
                  createNewHoliday(holiday)
                }}>
          <RiCheckmarkLine/>
        </Button>
      </div>
    ))}
  </FormProvider>
)

const Holiday = () => {
  const dispatch = useAppDispatch()
  const holidayState = useSelector((state: RootState) => state.holiday.state)
  const venueState = useSelector((state: RootState) => state.userInfo.state.venues)
  const [existingHolidays, setExistingHolidays] = useState([])
  const [newHolidays, setNewHolidays] = useState([])
  const [calendar, setCalendar] = useState({})
  const [holidayIdx, setHolidayIdx] = useState(0)
  const holidayMethods = useForm()

  const deleteExistingHoliday = async (holiday: Holiday) => {
    await dispatch(deleteHoliday(holiday.id))
    await dispatch(getHolidayList())
  }

  const createNewHoliday = async (holiday: Holiday) => {
    const selectedHolidays = {...holidayMethods.getValues('holiday')}
    const { name, date, venue } = selectedHolidays[holiday.id]
    const payload = {
      name,
      start_date: convertLocalToWita(date) || convertLocalToWita(new Date().getTime()),
      end_date: convertLocalToWita(date) || convertLocalToWita(new Date().getTime()),
      venue_holidays: [
        { venue_id: venue?.value },
      ]
    }
    setNewHolidays(newHolidays.filter(day => day.id !== holiday.id))
    await dispatch(createHoliday(payload))
    await dispatch(getHolidayList())
  }

  const removeNewHoliday = (holiday: Holiday) => setNewHolidays(newHolidays.filter(day => day.id !== holiday.id))

  const addNewHolidayForm = () => {
    setNewHolidays([...newHolidays, { start_date: new Date().getTime(), name: '', id: holidayIdx }])
    setCalendar({...calendar, [holidayIdx]: new Date().getTime()})
    setHolidayIdx(holidayIdx+1)
  }

  useEffect(() => {
    (async () => {
      await dispatch(getHolidayList())
      await dispatch(getUserVenues({}))
    })()
    setExistingHolidays([...holidayState])
  }, [])

  useEffect(() => {
    setExistingHolidays([...holidayState])
  }, [newHolidays, dispatch, getHolidayList, holidayState])

  return (
    <BreadcrumbLayout>
      <div className='flex flex-col w-full gap-8'>
        <div className='flex flex-col p-0'>
          <div className='flex pl-8 pt-8 bg-white rounded-t'>
            <p className='font-bold text-lg'>
              Set Holiday
            </p>
          </div>
          <div className='flex flex-col gap-2 p-7 bg-white'>
            {renderExistingHolidays({ holidayMethods }, existingHolidays, deleteExistingHoliday)}
            {renderNewHolidays({ holidayMethods }, newHolidays, venueState, calendar, setCalendar, createNewHoliday, removeNewHoliday)}
            <div className="w-full">
              <Button type="button"
                      variant="tertierry"
                      className="font-medium text-sm"
                      onClick={() => addNewHolidayForm()}>
                <RiAddLine/>
                Add Holiday
              </Button>
            </div>
          </div>
        </div>
      </div>
    </BreadcrumbLayout>
  );
}

export default Holiday
